import { defaultLayerInfo, type LayerInfo, type LayerLibraryRow, type ScopeTabOption } from '@civilgrid/ui/components';
import { useMemo } from 'react';
import type { AvailableLayer } from '~/api/availableLayers';
import type { TilesConfig } from '~/store2/layers.data';
import { digitizedConstituentKeys, squashDigitizedLayers, withDigitizedMembership } from './digitizedLibraryGroup';
import {
  filterLayersForTab,
  filterSiteDataFromCatalog,
  type LayerLibraryTab,
  layerLibraryTabOptions,
  rowTilesConfigEntry,
  selectedLayers,
  toLayerLibraryRow,
  unselectedLayers,
} from './helpers';
import { LayerPreview } from './LayerPreviewMap';
import { derivePreviewStyle } from './layerAssets';

export interface LayerLibraryCatalogParams {
  catalog: AvailableLayer[] | undefined;
  libraryLayerIds: ReadonlySet<string>;
  tab: LayerLibraryTab;
  tilesConfig: TilesConfig | undefined;
  customLayersTilesConfig: TilesConfig | undefined;
}

export interface LayerLibraryCatalog {
  // digitizedConstituentKeys returns an array (withDigitizedMembership takes it as `readonly string[]`,
  // consumers only iterate it) — kept as an array rather than a Set for behavior/type fidelity.
  constituentKeys: readonly string[];
  catalogRows: LayerLibraryRow[];
  selectedRows: LayerLibraryRow[];
  /** True when the tab has layers and every one of them is already added. */
  allTabLayersAdded: boolean;
  tabOptions: ScopeTabOption[];
  getLayerInfo: (row: LayerLibraryRow) => LayerInfo;
}

/**
 * Pure derivation pipeline shared by the user and org Layer Library pages: catalog →
 * squashed/digitized display rows → tab-filtered catalog/selected rows → popover info.
 * No hooks, no valtio/store coupling — every input is injected, so both pages' membership
 * semantics (visibility+pinned vs membership-only) stay at the call site.
 */
export function deriveLayerLibraryCatalog(params: LayerLibraryCatalogParams): LayerLibraryCatalog {
  const { catalog, libraryLayerIds, tab, tilesConfig, customLayersTilesConfig } = params;

  const mergedTilesConfig =
    tilesConfig || customLayersTilesConfig ? { ...tilesConfig, ...customLayersTilesConfig } : undefined;

  // The Library renders the squashed display catalog (digitized branch → one row);
  // the store keeps the raw grain above. Membership mirrors the squash: the synthetic
  // row is "added" while any constituent group is, and add/remove fan out below.
  // Site Data is display-filtered out first: it is not library-curated (membership is
  // server-guaranteed, so there is nothing to Add/Remove), covering the catalog table,
  // the Selected panel, and the tab counts on both the user and org Libraries.
  const displayCatalog = squashDigitizedLayers(filterSiteDataFromCatalog(catalog ?? []));
  const constituentKeys = digitizedConstituentKeys(catalog ?? []);
  const displayLibraryIds = withDigitizedMembership(libraryLayerIds, constituentKeys);

  // The catalog shows only layers still available to add — added layers move to the
  // Selected table rather than rendering an "Added" row state.
  const tabCatalog = filterLayersForTab(tab, displayCatalog);
  const catalogRows = unselectedLayers(tabCatalog, displayLibraryIds).map((layer) =>
    toLayerLibraryRow(layer, mergedTilesConfig),
  );
  const selectedRows = selectedLayers(displayCatalog, displayLibraryIds).map((layer) =>
    toLayerLibraryRow(layer, mergedTilesConfig),
  );
  // A tab with no layers at all (e.g. `special` with no entitlements) is not "all added".
  const allTabLayersAdded = tabCatalog.length > 0 && catalogRows.length === 0;
  const tabOptions = layerLibraryTabOptions(displayCatalog, displayLibraryIds);

  // Popover detail: the shared default info plus the live-map preview for layers whose
  // representative sub-layer has a drawable tiles-config style (raster/missing →
  // placeholders). Representative = first sub-layer, except point-first groups prefer
  // their first line/polygon sub-layer (rowTilesConfigEntry, Figma #21).
  const catalogByKey = new Map(displayCatalog.map((layer) => [layer.key, layer]));
  const getLayerInfo = (row: LayerLibraryRow): LayerInfo => {
    const layer = catalogByKey.get(row.id);
    const previewStyle = derivePreviewStyle(layer && rowTilesConfigEntry(layer, mergedTilesConfig));
    return {
      ...defaultLayerInfo(row),
      preview: previewStyle ? <LayerPreview style={previewStyle} /> : undefined,
    };
  };

  return {
    constituentKeys,
    catalogRows,
    selectedRows,
    allTabLayersAdded,
    tabOptions,
    getLayerInfo,
  };
}

/** Thin memo wrapper — recomputes only when one of the 5 inputs changes. */
export function useLayerLibraryCatalog(params: LayerLibraryCatalogParams): LayerLibraryCatalog {
  const { catalog, libraryLayerIds, tab, tilesConfig, customLayersTilesConfig } = params;
  return useMemo(
    () =>
      deriveLayerLibraryCatalog({
        catalog,
        libraryLayerIds,
        tab,
        tilesConfig,
        customLayersTilesConfig,
      }),
    [catalog, libraryLayerIds, tab, tilesConfig, customLayersTilesConfig],
  );
}
